/* import __COLOCATED_TEMPLATE__ from './circuit-race-status-card.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { use } from 'ember-resources';
import { socketRecordTable } from 'bullitttime-embercomponents/utils/socket-record-table-resource';
import dayjs from 'dayjs';

// Mirrors the pattern in components/timing/intermediate-timing-record-table.js:
// a SocketRecordTable seeded from a REST fetch and kept live via socketGuru's
// "intermediateTimingRecord" events.
export default class CircuitRaceStatusCardComponent extends Component
{
	@service ajax;
	@service socketGuru;

	@tracked selectedTimingPointId = 0;

	// Rebuilds when @event or the selected timing point changes — the latter
	// replaces the manual refetch the dropdown used to trigger. A stale in-flight
	// fetch resolves against the discarded table instance, so the old
	// selection-race guard is no longer needed.
	@use recordTable = socketRecordTable(() =>
	{
		const { event } = this.args;
		const timingPointId = this.selectedTimingPointId;
		return {
			socketGuru: this.socketGuru,
			ajax: this.ajax,
			channels: 'intermediateTimingRecord',
			endpoint: () => (timingPointId && timingPointId != 0)
				? `/intermediate-timing-points/${timingPointId}/timingRecords`
				: null,
			transform: (record) => this.transformRecord(record, event, timingPointId),
		};
	});

	transformRecord(record, event, timingPointId)
	{
		// Skip records from a different event or timing point.
		if(event && record.eventId != event.id)
		{
			return null;
		}
		if(record.timingPointId != timingPointId)
		{
			return null;
		}
		return record;
	}

	@action
	timingPointSelectionChanged(browserEvent)
	{
		const value = browserEvent.target.value;
		this.selectedTimingPointId = (value == '-') ? 0 : value;
	}

	get sortedIntermediateTimingPoints()
	{
		const points = (this.args.intermediateTimingPoints || []).slice();
		points.sort((a, b) =>
		{
			const aid = Number(a.id);
			const bid = Number(b.id);
			return aid - bid;
		});
		return points;
	}

	// Group timing records by EntryId (string-keyed for safety across the
	// socket payload and the Ember Data ids).
	get recordsByEntryId()
	{
		const map = new Map();
		for(const r of this.recordTable.records)
		{
			if(r.EntryId == null) continue;
			const key = String(r.EntryId);
			if(!map.has(key)) map.set(key, []);
			map.get(key).push(r);
		}
		// Sort each entry's records by impulseTime ascending.
		for(const records of map.values())
		{
			records.sort((a, b) =>
			{
				const aTime = a.impulseTime ? dayjs(a.impulseTime).valueOf() : 0;
				const bTime = b.impulseTime ? dayjs(b.impulseTime).valueOf() : 0;
				return aTime - bTime;
			});
		}
		return map;
	}

	// One entry per class group. Returns:
	//   [{classGroup, course, numLaps, leaderLapCount, leaderFinishTime,
	//     missingNumLaps: bool, rows: [...]}]
	// rows are sorted: finished first (lapCount desc, lastCrossingTime asc),
	// then racing rows (lapCount desc, lastCrossingTime asc).
	get statusByClassGroup()
	{
		const classGroupData = this.args.classGroupData || [];
		const recordsByEntryId = this.recordsByEntryId;
		const hasSelection = this.selectedTimingPointId && this.selectedTimingPointId != 0;

		return classGroupData.map(({ classGroup, course, entriesByClass }) =>
		{
			const numLaps = course?.numLaps;
			const hasNumLaps = numLaps != null && Number(numLaps) > 0;

			// Flatten to an array of {entry, cls}.
			const entryWithClass = [];
			for(const group of entriesByClass)
			{
				for(const entry of group.entries)
				{
					entryWithClass.push({ entry, cls: group.cls });
				}
			}

			// Per-entry computed {lapCount, lastCrossingTime, records}.
			const perEntry = entryWithClass.map(({ entry, cls }) =>
			{
				const records = recordsByEntryId.get(String(entry.id)) || [];
				const lapCount = records.length;
				const lastCrossingTime = lapCount > 0 ? records[lapCount - 1].impulseTime : null;
				return { entry, cls, records, lapCount, lastCrossingTime };
			});

			let leaderLapCount = 0;
			let leaderFinishTime = null;
			let rows = [];

			// maxDiffByEntryId: highest (leader.lapCount - rider.lapCount) ever
			// observed for each rider over the class group's crossing timeline.
			// It only goes up, so a rider that's been lapped stays lapped even
			// after they cross the line again. Display lapsDown =
			// max(0, maxDiff - 1): being one lap behind in count just means
			// trailing on the same lap; "-1" only appears once the leader has
			// actually overlapped them.
			const maxDiffByEntryId = new Map();

			if(hasSelection && hasNumLaps)
			{
				// Walk all crossings in this class group in time order and
				// maintain leaderLapCount + per-entry lapCount. Whenever the
				// leader bumps, every other entry's running max diff may
				// increase.
				const timeline = [];
				for(const p of perEntry)
				{
					for(const r of p.records)
					{
						timeline.push({ entryId: String(p.entry.id), impulseTime: r.impulseTime });
					}
				}
				timeline.sort((a, b) => dayjs(a.impulseTime).valueOf() - dayjs(b.impulseTime).valueOf());

				const lapCounts = new Map();
				for(const p of perEntry)
				{
					const id = String(p.entry.id);
					lapCounts.set(id, 0);
					maxDiffByEntryId.set(id, 0);
				}

				for(const cross of timeline)
				{
					const eid = cross.entryId;
					const newLap = (lapCounts.get(eid) || 0) + 1;
					lapCounts.set(eid, newLap);
					if(newLap > leaderLapCount)
					{
						leaderLapCount = newLap;
						// Leader pulled ahead - update every other rider's
						// running max diff if it grew.
						for(const p of perEntry)
						{
							const otherId = String(p.entry.id);
							const otherLap = lapCounts.get(otherId) || 0;
							const diff = leaderLapCount - otherLap;
							const prev = maxDiffByEntryId.get(otherId) || 0;
							if(diff > prev)
							{
								maxDiffByEntryId.set(otherId, diff);
							}
						}
					}
				}

				if(leaderLapCount >= numLaps)
				{
					// Earliest numLaps-th crossing among entries that reached numLaps.
					for(const p of perEntry)
					{
						if(p.lapCount >= numLaps)
						{
							const finishRecord = p.records[numLaps - 1];
							const t = finishRecord?.impulseTime;
							if(t && (leaderFinishTime == null || dayjs(t).isBefore(dayjs(leaderFinishTime))))
							{
								leaderFinishTime = t;
							}
						}
					}
				}

				rows = perEntry.map((p) =>
				{
					const { entry, cls, lapCount, lastCrossingTime } = p;
					let status;
					let finished = false;
					let displayLapsDown = 0;

					if(lapCount >= numLaps)
					{
						status = "Finished";
						finished = true;
						displayLapsDown = 0;
					}
					else if(leaderFinishTime != null && lastCrossingTime != null
						&& dayjs(lastCrossingTime).isAfter(dayjs(leaderFinishTime)))
					{
						status = "Finished";
						finished = true;
						displayLapsDown = numLaps - lapCount;
					}
					else
					{
						status = "Lap " + lapCount + " / " + numLaps;
						const maxDiff = maxDiffByEntryId.get(String(entry.id)) || 0;
						displayLapsDown = Math.max(0, maxDiff - 1);
					}

					return {
						entry,
						cls,
						lapCount,
						lastCrossingTime,
						status,
						finished,
						displayLapsDown,
						lapProgress: lapCount + " / " + numLaps
					};
				});

				rows.sort((a, b) =>
				{
					// Finished rows first.
					if(a.finished && !b.finished) return -1;
					if(!a.finished && b.finished) return 1;
					// Within a bucket: more laps first.
					if(a.lapCount !== b.lapCount) return b.lapCount - a.lapCount;
					// Then earliest lastCrossingTime first (the first to reach that lap).
					const at = a.lastCrossingTime ? dayjs(a.lastCrossingTime).valueOf() : Infinity;
					const bt = b.lastCrossingTime ? dayjs(b.lastCrossingTime).valueOf() : Infinity;
					if(at !== bt) return at - bt;
					// Fallback: bib order (missing bibs at end).
					const ab = a.entry?.bib;
					const bb = b.entry?.bib;
					if(ab == null && bb == null) return 0;
					if(ab == null) return 1;
					if(bb == null) return -1;
					return Number(ab) - Number(bb);
				});
			}
			else
			{
				// No selection or no numLaps: just list the entries with blanks.
				rows = perEntry.map((p) => ({
					entry: p.entry,
					cls: p.cls,
					lapCount: p.lapCount,
					lastCrossingTime: p.lastCrossingTime,
					status: hasSelection ? "" : "",
					finished: false,
					displayLapsDown: 0,
					lapProgress: ""
				}));
				rows.sort((a, b) =>
				{
					const ab = a.entry?.bib;
					const bb = b.entry?.bib;
					if(ab == null && bb == null) return 0;
					if(ab == null) return 1;
					if(bb == null) return -1;
					return Number(ab) - Number(bb);
				});
			}

			let leaderFinishTimeFormatted = null;
			if(leaderFinishTime)
			{
				const d = dayjs(leaderFinishTime);
				if(d.isValid())
				{
					leaderFinishTimeFormatted = d.format("HH:mm:ss");
				}
			}

			return {
				classGroup,
				course,
				numLaps,
				missingNumLaps: !hasNumLaps,
				leaderLapCount,
				leaderFinishTime,
				leaderFinishTimeFormatted,
				rows
			};
		});
	}
}
